import { useState, useEffect } from "react";
import { Modal, Select } from "antd";
import { notify } from "@/shared/utils/notify";
import { productCategoriesService, transferProductsService } from "../../../shared/services";
import { IProductCategory } from "../../../redux/@types";
import { refreshTableProductCategories } from "../../../redux";
import useAppDispatch from "../../../redux/hooks/useAppDispatch";

interface Props {
  open: boolean;
  onClose: () => void;
  sourceCategory: IProductCategory | null;
  onSuccess?: () => void;
}

export const ModalTransferProducts = ({
  open,
  onClose,
  sourceCategory,
  onSuccess,
}: Props) => {
  const dispatch = useAppDispatch();
  const [targetCategoryId, setTargetCategoryId] = useState<string | null>(null);
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    if (open) {
      setTargetCategoryId(null);
      const loadCategories = async () => {
        setLoadingCategories(true);
        try {
          const res = await productCategoriesService.find({
            query: {
              $select: ["id", "name"],
              $limit: 5000,
            },
          });
          const data = Array.isArray(res) ? res : (res as any)?.data || [];
          setCategories(
            data
              .filter((c: IProductCategory) => c.id !== sourceCategory?.id)
              .map((c: IProductCategory) => ({
                value: String(c.id),
                label: c.name,
              }))
          );
        } catch (err: any) {
          notify.error(err?.message || "Error al cargar categorías");
        } finally {
          setLoadingCategories(false);
        }
      };
      loadCategories();
    }
  }, [open, sourceCategory?.id]);

  const handleTransfer = async () => {
    if (!sourceCategory || !targetCategoryId) {
      notify.warning("Selecciona la categoría destino");
      return;
    }
    setLoading(true);
    try {
      const res = await transferProductsService.create({
        from_category_id: sourceCategory.id,
        to_category_id: Number(targetCategoryId),
      } as any);
      const result = res as { transferred?: number; message?: string };
      notify.success(result?.message || `Se transfirieron ${result?.transferred ?? 0} productos`);
      onClose();
      onSuccess?.();
      dispatch(refreshTableProductCategories(true));
    } catch (err: any) {
      notify.error(err?.message || "Error al transferir productos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Transferir productos a otra categoría"
      open={open}
      onCancel={onClose}
      onOk={handleTransfer}
      okText="Transferir"
      confirmLoading={loading}
      destroyOnClose
      okButtonProps={{ disabled: !targetCategoryId }}
    >
      {sourceCategory && (
        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>Origen:</strong> {sourceCategory.name} (ID: {sourceCategory.id})
          </p>
          <p>
            <strong>Productos a transferir:</strong> {sourceCategory.products_count ?? 0}
          </p>
        </div>
      )}
      <div>
        <label style={{ display: "block", marginBottom: 8 }}>
          <strong>Categoría destino:</strong>
        </label>
        <Select
          placeholder="Selecciona la categoría destino"
          style={{ width: "100%" }}
          value={targetCategoryId}
          onChange={setTargetCategoryId}
          loading={loadingCategories}
          showSearch
          optionFilterProp="label"
          options={categories}
        />
      </div>
    </Modal>
  );
};
